import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { SectionHeading } from "./ui";

const SAMPLE_NGN = 1000;
const fmt = (n) => `₦${Math.round(n).toLocaleString("en-NG")}`;

export default function WatchPartyPackagesEditor() {
  const [pkgs, setPkgs] = useState(null);
  const [defaults, setDefaults] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/watch-party/packages").then(({ data }) => {
      setPkgs(data.packages);
      setDefaults(data.defaults);
    }).catch(() => toast.error("Couldn't load Watch Party packages"));
  }, []);

  const edit = (id, key, value) => setPkgs((ps) => ps.map((p) => (p.id === id ? { ...p, [key]: value } : p)));

  const save = async () => {
    setSaving(true);
    try {
      const body = { packages: pkgs.map((p) => ({ id: p.id, viewers: Number(p.viewers), multiplier: Number(p.multiplier) })) };
      const { data } = await api.put("/admin/watch-party/packages", body);
      setPkgs(data.packages);
      toast.success("Watch Party packages saved — new prices apply immediately");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? "Viewers must be 2–500 and multiplier above 0 (max 20)" : d || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!pkgs) return null;
  return (
    <section data-testid="wp-packages-editor">
      <SectionHeading
        eyebrow="Pricing"
        title="Watch Party packages"
        description="Price = film PPV price × viewers × multiplier. Viewers includes the host. 70/30 split applies to every party."
        action={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setPkgs(defaults)} className="border border-white/10" data-testid="wp-packages-reset">
              <RotateCcw className="w-4 h-4" /> Defaults
            </Button>
            <Button onClick={save} disabled={saving} className="bg-gold text-ink hover:bg-gold/90" data-testid="wp-packages-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </Button>
          </div>
        }
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {pkgs.map((p) => (
          <div key={p.id} className="rounded-2xl border border-white/10 bg-surface p-5" data-testid={`wp-package-${p.id}`}>
            <p className="font-display text-lg font-bold">{p.name}</p>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <label className="text-[11px] uppercase tracking-widest text-foreground/50">
                Viewers
                <Input type="number" min={2} max={500} value={p.viewers} onChange={(e) => edit(p.id, "viewers", e.target.value)} className="mt-1 bg-white/5 border-white/10 text-foreground text-base" data-testid={`wp-package-${p.id}-viewers`} />
              </label>
              <label className="text-[11px] uppercase tracking-widest text-foreground/50">
                Multiplier
                <Input type="number" min={0.1} max={20} step={0.1} value={p.multiplier} onChange={(e) => edit(p.id, "multiplier", e.target.value)} className="mt-1 bg-white/5 border-white/10 text-foreground text-base" data-testid={`wp-package-${p.id}-multiplier`} />
              </label>
            </div>
            <p className="text-xs text-foreground/55 mt-4" data-testid={`wp-package-${p.id}-example`}>
              ₦1,000 film → <strong className="text-gold">{fmt(SAMPLE_NGN * (Number(p.viewers) || 0) * (Number(p.multiplier) || 0))}</strong>
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}
